/**
 * Servicio de Caché en memoria para el Panel Administrativo (Flota Empresarial y Plantilla de Conductores)
 * TTL: 15 minutos por NIT / UID
 */
import { auth, db } from "@/firebase";
import store from "@/store/index.js";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import moment from "moment";

const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutos

const cacheArranque = {};
const cacheFlota = {};
const cachePlantilla = {};

/**
 * Normaliza el tipo de documento a la nomenclatura oficial del sistema
 * @param {string} tipo
 * @returns {string}
 */
function normalizarTipoDocumento(tipo) {
  if (!tipo || typeof tipo !== "string") return "-";
  const t = tipo.trim().toLowerCase();

  if (t === "cc" || t === "c.c" || t === "c.c." || t.includes("ciudadan") || t === "cedula") {
    return "Cédula de Ciudadanía";
  }
  if (t === "ce" || t === "c.e" || t === "c.e." || t.includes("extranjer")) {
    return "Cédula de Extranjería";
  }
  if (t === "ti" || t === "t.i" || t === "t.i." || t.includes("tarjeta")) {
    return "Tarjeta de Identidad";
  }
  if (t === "pep" || t === "p.e.p" || t.includes("pep")) {
    return "PEP - Venezolanos";
  }
  if (t === "ppt" || t === "p.p.t" || t.includes("ppt") || t.includes("proteccion") || t.includes("temporal")) {
    return "PPT - Venezolanos";
  }
  if (t === "pa" || t === "pas" || t.includes("pasaporte")) {
    return "Pasaporte";
  }
  if (t === "rc" || t === "r.c" || t === "r.c." || t.includes("registro civil")) {
    return "Registro Civil";
  }
  if (t === "nit") {
    return "NIT";
  }

  return tipo.trim() || "-";
}

/**
 * Normaliza el teléfono o celular, extrayendo el número si es un objeto
 * @param {any} tel
 * @returns {string}
 */
function normalizarTelefono(tel) {
  if (!tel) return "-";
  if (typeof tel === "object") {
    return tel.numero ? String(tel.numero).trim() || "-" : "-";
  }
  return String(tel).trim() || "-";
}

class CacheAdministrativoService {
  /**
   * Obtiene la estructura de arranque (usuario, empresa seleccionada, sedes activas asociadas al NIT y validación de privilegios)
   * @param {string} uid
   * @param {boolean} forceRefresh
   * @returns {Promise<{autorizado: boolean, error?: string, usuario?: Object, empresa?: Object, nitNumero?: string, idsEmpresas?: Array<string>, idToRazonSocial?: Object, idToEspacioTrabajo?: Object}>}
   */
  async obtenerEmpresas(uid, forceRefresh = false) {
    if (!uid) {
      return { autorizado: false, error: "NO_UID" };
    }

    const now = Date.now();
    if (!forceRefresh && cacheArranque[uid] && (now - cacheArranque[uid].timestamp < CACHE_TTL_MS)) {
      return cacheArranque[uid].data;
    }

    try {
      const usuario = await store.state.obtenerUsuario(uid);
      if (!usuario || !usuario.preferencias || !usuario.preferencias.idEmpresaSeleccionada) {
        return { autorizado: false, error: "NO_EMPRESA_SELECCIONADA" };
      }

      const idEmpresaSeleccionada = usuario.preferencias.idEmpresaSeleccionada;

      // Obtener empresa seleccionada
      const empresaSeleccionada = await store.state.obtenerEmpresa(idEmpresaSeleccionada);
      if (!empresaSeleccionada || !empresaSeleccionada.nit || !empresaSeleccionada.nit.numero) {
        return { autorizado: false, error: "NO_EMPRESA_DATA" };
      }

      const nitNumero = empresaSeleccionada.nit.numero;

      // Consultar únicamente espacios de trabajo activos (Creado/Editado) con el mismo NIT
      const qEmpresas = query(
        collection(db, "riskmann_empresa"),
        where("nit.numero", "==", nitNumero),
        where("registro.estado", "in", ["Creado", "Editado"])
      );
      const snapshotEmpresas = await getDocs(qEmpresas);

      const idsEmpresas = [];
      const idToRazonSocial = {};
      const idToEspacioTrabajo = {};

      snapshotEmpresas.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const id = docSnap.id;
        idsEmpresas.push(id);
        idToRazonSocial[id] = data.razonSocial || "N/A";
        const sede = data.nit && data.nit.consecutivo > 0
          ? ` - SUBESPACIO #${data.nit.consecutivo}`
          : " - PRINCIPAL";
        idToEspacioTrabajo[id] = `${data.razonSocial || "N/A"}${sede}`;
      });

      const arranqueData = {
        autorizado: true,
        usuario,
        empresa: empresaSeleccionada,
        nitNumero,
        idsEmpresas,
        idToRazonSocial,
        idToEspacioTrabajo,
      };

      cacheArranque[uid] = {
        timestamp: Date.now(),
        data: arranqueData,
      };

      return arranqueData;
    } catch (error) {
      console.error("Error en getArranque de CacheAdministrativoService:", error);
      return { autorizado: false, error: error.message || "ERROR_DESCONOCIDO" };
    }
  }

  /**
   * Obtiene la flota vehicular consolidada para el NIT
   * @param {string} nitNumero
   * @param {Array<string>} idsEmpresas
   * @param {Object} idToEspacioTrabajo
   * @param {boolean} forceRefresh
   * @returns {Promise<Array<Object>>}
   */
  async obtenerFlota(nitNumero, idsEmpresas, idToEspacioTrabajo, forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && cacheFlota[nitNumero] && (now - cacheFlota[nitNumero].timestamp < CACHE_TTL_MS)) {
      return cacheFlota[nitNumero].data;
    }

    if (!idsEmpresas || idsEmpresas.length === 0) {
      const vacio = Object.freeze([]);
      cacheFlota[nitNumero] = { timestamp: Date.now(), data: vacio };
      return vacio;
    }

    // Dividir en chunks de 10
    const chunks = [];
    for (let i = 0; i < idsEmpresas.length; i += 10) {
      chunks.push(idsEmpresas.slice(i, i + 10));
    }

    const promesasVehiculos = chunks.map((chunk) => {
      const qVehiculos = query(
        collection(db, "riskmann_seguridadvial_vehiculos"),
        where("idEmpresa", "in", chunk)
      );
      return getDocs(qVehiculos);
    });

    const snapshotsVehiculos = await Promise.all(promesasVehiculos);

    let docsData = [];
    snapshotsVehiculos.forEach((querySnapshot) => {
      querySnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (
          data.registro &&
          ["Creado", "Editado", "Eliminado"].includes(data.registro.estado) &&
          data.placa &&
          data.placa.trim() !== ""
        ) {
          docsData.push({ id: docSnap.id, ...data, docReference: docSnap });
        }
      });
    });

    // Ordenar por fecha de creación (más reciente primero)
    docsData.sort((a, b) => {
      let dateA = a.registro && a.registro.creacion && typeof a.registro.creacion.toMillis === "function"
        ? a.registro.creacion.toMillis()
        : 0;
      let dateB = b.registro && b.registro.creacion && typeof b.registro.creacion.toMillis === "function"
        ? b.registro.creacion.toMillis()
        : 0;
      return dateB - dateA;
    });

    const vehiculos = Object.freeze(
      docsData.map((data, index) => {
        let fechaFormateada = "-";
        if (data.registro && data.registro.creacion) {
          try {
            const docData = data.docReference && typeof data.docReference.data === "function"
              ? data.docReference.data({ serverTimestamps: "estimate" })
              : data;
            if (docData.registro && docData.registro.creacion && typeof docData.registro.creacion.toDate === "function") {
              fechaFormateada = moment(docData.registro.creacion.toDate()).format("DD/MM/YYYY hh:mm A");
            }
          } catch (e) {
            console.warn("Error formateando fecha vehiculo", e);
          }
        }

        const espacioTrabajo = (idToEspacioTrabajo && idToEspacioTrabajo[data.idEmpresa]) || "-";

        return Object.freeze({
          consecutivo: index + 1,
          id: data.id,
          idEmpresa: data.idEmpresa,
          placa: data.placa ? data.placa : "-",
          empresa: espacioTrabajo,
          espacioTrabajo: espacioTrabajo,
          marca: data.marca ? data.marca : "-",
          modelo: data.modelo ? data.modelo : "-",
          clase: data.clase ? data.clase : "-",
          tipoVinculacion: data.tipoVinculacion ? data.tipoVinculacion : "-",
          estadoVehiculo: data.registro && data.registro.estado === "Eliminado" ? "Inactivo" : "Activo",
          dispositivoZonaCliente: data.dispositivoZonaCliente ? data.dispositivoZonaCliente : "-",
          registro: fechaFormateada,
          registroCreacionMillis:
            data.registro && data.registro.creacion && typeof data.registro.creacion.toMillis === "function"
              ? data.registro.creacion.toMillis()
              : 0,
        });
      })
    );

    cacheFlota[nitNumero] = {
      timestamp: Date.now(),
      data: vehiculos,
    };

    return vehiculos;
  }

  /**
   * Obtiene la plantilla de conductores consolidada para el NIT
   * @param {string} nitNumero
   * @param {Array<string>} idsEmpresas
   * @param {Object} idToEspacioTrabajo
   * @param {boolean} forceRefresh
   * @returns {Promise<Array<Object>>}
   */
  async obtenerConductores(nitNumero, idsEmpresas, idToEspacioTrabajo, forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && cachePlantilla[nitNumero] && (now - cachePlantilla[nitNumero].timestamp < CACHE_TTL_MS)) {
      return cachePlantilla[nitNumero].data;
    }

    if (!idsEmpresas || idsEmpresas.length === 0) {
      const vacio = Object.freeze([]);
      cachePlantilla[nitNumero] = { timestamp: Date.now(), data: vacio };
      return vacio;
    }

    // Dividir en chunks de 10
    const chunks = [];
    for (let i = 0; i < idsEmpresas.length; i += 10) {
      chunks.push(idsEmpresas.slice(i, i + 10));
    }

    const snapshots = await Promise.all(
      chunks.map((chunk) =>
        getDocs(
          query(
            collection(db, "riskmann_seguridadvial_conductores"),
            where("idEmpresa", "in", chunk)
          )
        )
      )
    );

    let docsData = [];
    snapshots.forEach((snap) => {
      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.registro && ["Creado", "Editado", "Eliminado"].includes(data.registro.estado)) {
          docsData.push({ id: docSnap.id, ...data, docReference: docSnap });
        }
      });
    });

    docsData.sort((a, b) => {
      const dateA = a.registro && a.registro.creacion && typeof a.registro.creacion.toMillis === "function"
        ? a.registro.creacion.toMillis()
        : 0;
      const dateB = b.registro && b.registro.creacion && typeof b.registro.creacion.toMillis === "function"
        ? b.registro.creacion.toMillis()
        : 0;
      return dateB - dateA;
    });

    // Batch lookup de usuarios con rodamiento
    const idsUsuarios = [
      ...new Set(
        docsData
          .filter((d) => d.conRodamiento === "Si" && d.idUsuario)
          .map((d) => d.idUsuario)
      ),
    ];

    const mapaUsuarios = {};
    await Promise.all(
      idsUsuarios.map(async (id) => {
        const u = await store.state.obtenerUsuario(id);
        if (u) mapaUsuarios[id] = u;
      })
    );

    // Omitir conductores inválidos:
    // - Con rodamiento: debe tener idUsuario y encontrarse en mapaUsuarios
    // - Sin rodamiento: debe contar con datos válidos en el objeto sinRodamiento
    const docsValidos = docsData.filter((data) => {
      const esConRodamiento = data.conRodamiento === "Si";
      if (esConRodamiento) {
        return !!(data.idUsuario && mapaUsuarios[data.idUsuario]);
      } else {
        const sr = data.sinRodamiento;
        return !!(
          sr &&
          typeof sr === "object" &&
          (
            (sr.nombre && String(sr.nombre).trim() !== "") ||
            (sr.apellido && String(sr.apellido).trim() !== "") ||
            (sr.documento && ((typeof sr.documento === "object" && sr.documento.numero && String(sr.documento.numero).trim() !== "") || (typeof sr.documento === "string" && sr.documento.trim() !== ""))) ||
            (sr.cedula && String(sr.cedula).trim() !== "") ||
            (sr.telefono && String(sr.telefono).trim() !== "")
          )
        );
      }
    });

    const conductores = Object.freeze(
      docsValidos.map((data, index) => {
        const esConRodamiento = data.conRodamiento === "Si";
        const usuario = esConRodamiento ? mapaUsuarios[data.idUsuario] : null;

        const nombre = esConRodamiento
          ? `${(usuario && usuario.nombre) || ""} ${(usuario && usuario.apellido) || ""}`.trim() || "-"
          : `${(data.sinRodamiento && data.sinRodamiento.nombre) || ""} ${(data.sinRodamiento && data.sinRodamiento.apellido) || ""}`.trim() || "-";

        const tipoDocumentoRaw = esConRodamiento
          ? (usuario && usuario.documento && usuario.documento.tipo) || "-"
          : (data.sinRodamiento && data.sinRodamiento.documento && data.sinRodamiento.documento.tipo) || "-";
        const tipoDocumento = normalizarTipoDocumento(tipoDocumentoRaw);

        const numeroDocumento = esConRodamiento
          ? String((usuario && usuario.documento && usuario.documento.numero) || "-")
          : String((data.sinRodamiento && data.sinRodamiento.documento && data.sinRodamiento.documento.numero) || "-");

        const telefonoRaw = esConRodamiento
          ? (usuario && (usuario.telefono || usuario.celular))
          : (data.sinRodamiento && (data.sinRodamiento.telefono || data.sinRodamiento.celular));
        const telefono = normalizarTelefono(telefonoRaw);

        const correo = esConRodamiento
          ? (usuario && (usuario.correo || usuario.email)) || "-"
          : (data.sinRodamiento && (data.sinRodamiento.correo || data.sinRodamiento.email)) || "-";

        const contacto = [telefono, correo].filter(v => v && v !== "-").join(" | ") || "-";

        let fechaFormateada = "-";
        if (data.registro && data.registro.creacion) {
          try {
            const docData = data.docReference && typeof data.docReference.data === "function"
              ? data.docReference.data({ serverTimestamps: "estimate" })
              : data;
            if (docData.registro && docData.registro.creacion && typeof docData.registro.creacion.toDate === "function") {
              fechaFormateada = moment(docData.registro.creacion.toDate()).format("DD/MM/YYYY hh:mm A");
            }
          } catch (e) {
            console.warn("Error formateando fecha conductor", e);
          }
        }

        return Object.freeze({
          consecutivo: index + 1,
          id: data.id,
          idEmpresa: data.idEmpresa,
          idUsuario: data.idUsuario,
          nombre,
          tipoDocumento,
          numeroDocumento,
          telefono,
          correo,
          contacto,
          espacioTrabajo: idToEspacioTrabajo[data.idEmpresa] || "-",
          dispositivoZonaCliente: data.dispositivoZonaCliente || "-",
          fechaRegistro: fechaFormateada,
          estadoOperativo: data.registro && data.registro.estado === "Eliminado" ? "Desvinculado" : "Activo",
          rodamiento: esConRodamiento ? "Con Rodamiento" : "Sin Rodamiento",
          registroCreacionMillis:
            data.registro && data.registro.creacion && typeof data.registro.creacion.toMillis === "function"
              ? data.registro.creacion.toMillis()
              : 0,
        });
      })
    );

    cachePlantilla[nitNumero] = {
      timestamp: Date.now(),
      data: conductores,
    };

    return conductores;
  }

  /**
   * Actualiza o remueve un vehículo individual en el caché en memoria
   * @param {string} id
   * @param {string} nitNumero
   * @param {Object} idToEspacioTrabajo
   * @returns {Promise<Array<Object>|null>}
   */
  async actualizarVehiculoIndividual(id, nitNumero, idToEspacioTrabajo) {
    if (!cacheFlota[nitNumero] || !cacheFlota[nitNumero].data) {
      return null;
    }
    try {
      const docRef = doc(db, "riskmann_seguridadvial_vehiculos", id);
      const docSnap = await getDoc(docRef);
      let items = [...cacheFlota[nitNumero].data];
      const index = items.findIndex((v) => v.id === id);

      if (!docSnap.exists()) {
        if (index !== -1) {
          items.splice(index, 1);
        }
      } else {
        const data = docSnap.data();
        const esValido =
          data.registro &&
          ["Creado", "Editado", "Eliminado"].includes(data.registro.estado) &&
          data.placa &&
          data.placa.trim() !== "";

        if (!esValido) {
          if (index !== -1) {
            items.splice(index, 1);
          }
        } else {
          let fechaFormateada = "-";
          if (data.registro && data.registro.creacion) {
            try {
              const docData = docSnap.data({ serverTimestamps: "estimate" });
              if (docData.registro && docData.registro.creacion && typeof docData.registro.creacion.toDate === "function") {
                fechaFormateada = moment(docData.registro.creacion.toDate()).format("DD/MM/YYYY hh:mm A");
              }
            } catch (e) {
              console.warn("Error formateando fecha en actualizacion individual", e);
            }
          }

          const espacioTrabajo = (idToEspacioTrabajo && idToEspacioTrabajo[data.idEmpresa]) || (index !== -1 ? (items[index].espacioTrabajo || items[index].empresa) : "-");

          const updatedRow = Object.freeze({
            consecutivo: index !== -1 ? items[index].consecutivo : items.length + 1,
            id: docSnap.id,
            idEmpresa: data.idEmpresa,
            placa: data.placa ? data.placa : "-",
            empresa: espacioTrabajo,
            espacioTrabajo: espacioTrabajo,
            marca: data.marca ? data.marca : "-",
            modelo: data.modelo ? data.modelo : "-",
            clase: data.clase ? data.clase : "-",
            tipoVinculacion: data.tipoVinculacion ? data.tipoVinculacion : "-",
            estadoVehiculo: data.registro && data.registro.estado === "Eliminado" ? "Inactivo" : "Activo",
            dispositivoZonaCliente: data.dispositivoZonaCliente ? data.dispositivoZonaCliente : "-",
            registro: fechaFormateada,
            registroCreacionMillis:
              data.registro && data.registro.creacion && typeof data.registro.creacion.toMillis === "function"
                ? data.registro.creacion.toMillis()
                : 0,
          });

          if (index !== -1) {
            items[index] = updatedRow;
          } else {
            items.unshift(updatedRow);
            items.sort((a, b) => b.registroCreacionMillis - a.registroCreacionMillis);
          }
        }
      }

      items = items.map((item, idx) => Object.freeze({ ...item, consecutivo: idx + 1 }));
      cacheFlota[nitNumero].data = Object.freeze(items);
      return cacheFlota[nitNumero].data;
    } catch (error) {
      console.error("Error al actualizar vehiculo individual en cache:", error);
      return null;
    }
  }

  /**
   * Actualiza o remueve un conductor individual en el caché en memoria
   * @param {string} id
   * @param {string} nitNumero
   * @param {Object} idToEspacioTrabajo
   * @returns {Promise<Array<Object>|null>}
   */
  async actualizarConductorIndividual(id, nitNumero, idToEspacioTrabajo) {
    if (!cachePlantilla[nitNumero] || !cachePlantilla[nitNumero].data) {
      return null;
    }
    try {
      const docRef = doc(db, "riskmann_seguridadvial_conductores", id);
      const docSnap = await getDoc(docRef);
      let items = [...cachePlantilla[nitNumero].data];
      const index = items.findIndex((c) => c.id === id);

      if (!docSnap.exists()) {
        if (index !== -1) {
          items.splice(index, 1);
        }
      } else {
        const data = docSnap.data();
        const esValido =
          data.registro && ["Creado", "Editado", "Eliminado"].includes(data.registro.estado);

        if (!esValido) {
          if (index !== -1) {
            items.splice(index, 1);
          }
        } else {
          const esConRodamiento = data.conRodamiento === "Si";
          let usuario = null;
          if (esConRodamiento) {
            if (data.idUsuario) {
              usuario = await store.state.obtenerUsuario(data.idUsuario);
            }
            if (!usuario) {
              // Si es con rodamiento y no se encontró su usuario, omitir/remover de la plantilla
              if (index !== -1) {
                items.splice(index, 1);
              }
              items = items.map((item, idx) => Object.freeze({ ...item, consecutivo: idx + 1 }));
              cachePlantilla[nitNumero].data = Object.freeze(items);
              return cachePlantilla[nitNumero].data;
            }
          } else {
            // Validar objeto sinRodamiento
            const sr = data.sinRodamiento;
            const tieneDatosSinRodamiento =
              sr &&
              typeof sr === "object" &&
              (
                (sr.nombre && String(sr.nombre).trim() !== "") ||
                (sr.apellido && String(sr.apellido).trim() !== "") ||
                (sr.documento && ((typeof sr.documento === "object" && sr.documento.numero && String(sr.documento.numero).trim() !== "") || (typeof sr.documento === "string" && sr.documento.trim() !== ""))) ||
                (sr.cedula && String(sr.cedula).trim() !== "") ||
                (sr.telefono && String(sr.telefono).trim() !== "")
              );

            if (!tieneDatosSinRodamiento) {
              // Si no cuenta con data en sinRodamiento, omitir/remover de la plantilla
              if (index !== -1) {
                items.splice(index, 1);
              }
              items = items.map((item, idx) => Object.freeze({ ...item, consecutivo: idx + 1 }));
              cachePlantilla[nitNumero].data = Object.freeze(items);
              return cachePlantilla[nitNumero].data;
            }
          }

          const nombre = esConRodamiento
            ? `${(usuario && usuario.nombre) || ""} ${(usuario && usuario.apellido) || ""}`.trim() || "-"
            : `${(data.sinRodamiento && data.sinRodamiento.nombre) || ""} ${(data.sinRodamiento && data.sinRodamiento.apellido) || ""}`.trim() || "-";

          const tipoDocumentoRaw = esConRodamiento
            ? (usuario && usuario.documento && usuario.documento.tipo) || "-"
            : (data.sinRodamiento && data.sinRodamiento.documento && data.sinRodamiento.documento.tipo) || "-";
          const tipoDocumento = normalizarTipoDocumento(tipoDocumentoRaw);

          const numeroDocumento = esConRodamiento
            ? String((usuario && usuario.documento && usuario.documento.numero) || "-")
            : String((data.sinRodamiento && data.sinRodamiento.documento && data.sinRodamiento.documento.numero) || "-");

          const telefonoRaw = esConRodamiento
            ? (usuario && (usuario.telefono || usuario.celular))
            : (data.sinRodamiento && (data.sinRodamiento.telefono || data.sinRodamiento.celular));
          const telefono = normalizarTelefono(telefonoRaw);

          const correo = esConRodamiento
            ? (usuario && (usuario.correo || usuario.email)) || "-"
            : (data.sinRodamiento && (data.sinRodamiento.correo || data.sinRodamiento.email)) || "-";

          const contacto = [telefono, correo].filter(v => v && v !== "-").join(" | ") || "-";

          let fechaFormateada = "-";
          if (data.registro && data.registro.creacion) {
            try {
              const docData = docSnap.data({ serverTimestamps: "estimate" });
              if (docData.registro && docData.registro.creacion && typeof docData.registro.creacion.toDate === "function") {
                fechaFormateada = moment(docData.registro.creacion.toDate()).format("DD/MM/YYYY hh:mm A");
              }
            } catch (e) {
              console.warn("Error formateando fecha conductor en actualizacion individual", e);
            }
          }

          const updatedRow = Object.freeze({
            consecutivo: index !== -1 ? items[index].consecutivo : items.length + 1,
            id: docSnap.id,
            idEmpresa: data.idEmpresa,
            idUsuario: data.idUsuario,
            nombre,
            tipoDocumento,
            numeroDocumento,
            telefono,
            correo,
            contacto,
            espacioTrabajo: (idToEspacioTrabajo && idToEspacioTrabajo[data.idEmpresa]) || (index !== -1 ? items[index].espacioTrabajo : "-"),
            dispositivoZonaCliente: data.dispositivoZonaCliente || "-",
            fechaRegistro: fechaFormateada,
            estadoOperativo: data.registro && data.registro.estado === "Eliminado" ? "Desvinculado" : "Activo",
            rodamiento: esConRodamiento ? "Con Rodamiento" : "Sin Rodamiento",
            registroCreacionMillis:
              data.registro && data.registro.creacion && typeof data.registro.creacion.toMillis === "function"
                ? data.registro.creacion.toMillis()
                : 0,
          });

          if (index !== -1) {
            items[index] = updatedRow;
          } else {
            items.unshift(updatedRow);
            items.sort((a, b) => b.registroCreacionMillis - a.registroCreacionMillis);
          }
        }
      }

      items = items.map((item, idx) => Object.freeze({ ...item, consecutivo: idx + 1 }));
      cachePlantilla[nitNumero].data = Object.freeze(items);
      return cachePlantilla[nitNumero].data;
    } catch (error) {
      console.error("Error al actualizar conductor individual en cache:", error);
      return null;
    }
  }

  /**
   * Obtiene el timestamp de la última carga de flota en caché
   * @param {string} nitNumero
   * @returns {number|null}
   */
  obtenerTimestampFlota(nitNumero) {
    return cacheFlota[nitNumero] ? cacheFlota[nitNumero].timestamp : null;
  }

  /**
   * Obtiene el timestamp de la última carga de plantilla de conductores en caché
   * @param {string} nitNumero
   * @returns {number|null}
   */
  obtenerTimestampConductores(nitNumero) {
    return cachePlantilla[nitNumero] ? cachePlantilla[nitNumero].timestamp : null;
  }

  /**
   * Limpia el caché en memoria
   * @param {string|null} nitNumero
   */
  limpiarCache(nitNumero = null) {
    if (nitNumero) {
      delete cacheFlota[nitNumero];
      delete cachePlantilla[nitNumero];
    } else {
      Object.keys(cacheArranque).forEach((k) => delete cacheArranque[k]);
      Object.keys(cacheFlota).forEach((k) => delete cacheFlota[k]);
      Object.keys(cachePlantilla).forEach((k) => delete cachePlantilla[k]);
    }
  }
}

const service = new CacheAdministrativoService();

// Limpiar cache al cerrar sesión
auth.onAuthStateChanged((user) => {
  if (!user) {
    service.limpiarCache();
  }
});

export default service;
